import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { GoogleMap, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from "../../../contexts/GoogleMapsContext";
import axios from "axios";
import { API_ENDPOINTS } from "../../../config/apiConfig";
import { create } from "lodash";

// Circuit colors for different circuit lines
const CIRCUIT_COLORS = [
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFA500', // Orange
  '#800080', // Purple
  '#00FFFF', // Cyan
  '#FF00FF', // Magenta
  '#FFD700', // Gold
  '#008000', // Dark Green
  '#4B0082', // Indigo
];

// Default map center (Southern California)
const DEFAULT_CENTER = { lat: 33.9, lng: -117.5 };
const DEFAULT_ZOOM = 10;

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Custom marker icons
const MARKER_PATHS = {
  depotMarker: '/img/map-markers/depot-marker.svg',
  chargerMarker: '/img/map-markers/charger-marker.svg',
  selectedDepotMarker: '/img/map-markers/depot-marker-selected.svg',
  selectedChargerMarker: '/img/map-markers/charger-marker-selected.svg',
  markerShadow: '/img/map-markers/marker-shadow.png'
};

const createMarkerIcons = (google) => {
  if (!google) return null;
  const size = (w, h) => new google.maps.Size(w, h);
  const point = (x, y) => new google.maps.Point(x, y);

  return {
    charger: {
      default: { url: MARKER_PATHS.chargerMarker, scaledSize: size(30, 49), anchor: point(15, 38) },
      selected: { url: MARKER_PATHS.selectedChargerMarker, scaledSize: size(30, 49), anchor: point(15, 38) },
    },
    depot: {
      default: { url: MARKER_PATHS.depotMarker, scaledSize: size(45, 73), anchor: point(22, 58) },
      selected: { url: MARKER_PATHS.selectedDepotMarker, scaledSize: size(45, 73), anchor: point(22, 58) },
    }
  };
};

export const AnalyticsMap = ({ 
  routes, 
  selectedRouteId = null,
  selectedDepot: selectedDepotId = null,
  selectedChargingStation: selectedChargingStationId = null,
  onChargingStationClick,
  showChargingStations = true
}) => {
  // State variables
  const [analyticsRoutes, setAnalyticsRoutes] = useState([]);
  const [depots, setDepots] = useState([]);
  const [chargingStations, setChargingStations] = useState([]);
  const [circuitData, setCircuitData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [icons, setIcons] = useState(null);
  // Load Google Maps API
  const { isLoaded, loadError } = useGoogleMaps();
  const routeColorCache = useRef({});

  useEffect(() => {
    if (isLoaded && window.google) {
      setIcons(createMarkerIcons(window.google));
    }
  }, [isLoaded]);

  // Fetch depots on mount
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    let isMounted = true;  // Add mounted flag

    const fetchDepots = async () => {
      try {

        const response = await axios.get(API_ENDPOINTS.getAllDepots, {
          headers: {
            Authorization: `Token ${authToken}`,
          },
        });

        if (Array.isArray(response.data)) {
          if (isMounted) {
            setDepots(response.data);
          }
        }
      } catch (error) {
        if (isMounted) {  // Only log error if still mounted
          console.error('Error fetching depots:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
          }
        }
      }
    };

    if (authToken) {
      fetchDepots();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Find the selected depot object from the depots array
  const selectedDepot = useMemo(() => 
    depots.find(depot => depot.depot_id === selectedDepotId),
    [depots, selectedDepotId]
  );

  // Fetch circuit data when selectedDepotId changes
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    let isMounted = true;
    const controller = new AbortController();

    const fetchCircuitData = async () => {
      if (!selectedDepotId || !authToken) {
        console.log('Missing depot ID or auth token');
        return;
      }

      if (isMounted) {
        setLoading(true);
      }

      try {
        // Find the selected depot
        const selectedDepotData = depots.find(d => d.depot_id === selectedDepotId);

        
        if (!selectedDepotData) {
          console.warn('Selected depot not found in depots array:', selectedDepotId);
          return;
        }
        
        
        // Extract circuit names from circuit_info
        let circuitNames = [];
        
        if (selectedDepotData.circuit_info && Array.isArray(selectedDepotData.circuit_info)) {
          // Extract the circuit names from the circuit_info array
          circuitNames = selectedDepotData.circuit_info.map(info => info.circuit);
        } else {
          console.warn('No circuit_info array found for depot:', selectedDepotId);
          return;
        }
        
        if (!circuitNames || circuitNames.length === 0) {
          console.warn('No circuit names found for depot:', selectedDepotId);
          return;
        }
        
        
        // Send circuit_names in the request body
        const response = await axios.post(API_ENDPOINTS.getCircuits, {
          circuit_names: circuitNames
        }, {
          headers: {
            Authorization: `Token ${authToken}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        if (isMounted) {
          if (response.data && response.data.circuits && Array.isArray(response.data.circuits)) {
            setCircuitData(prevData => ({
              ...prevData,
              [selectedDepotId]: response.data.circuits
            }));
          }
        }
      } catch (error) {
        if (error.name === 'CanceledError') {
          console.log('Request was cancelled');
        } else if (isMounted) {
          console.error('Error fetching circuit data:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCircuitData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedDepotId, depots]);

  // Fetch charging stations on mount
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    let isMounted = true;

    const fetchChargingStations = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.getAllChargingStations, {
          headers: {
            Authorization: `Token ${authToken}`,
          },
        });

        if (Array.isArray(response.data)) {
          if (isMounted) {
            setChargingStations(response.data);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching charging stations:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
          }
        }
      }
    };

    if (authToken) {
      fetchChargingStations();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // Cleanup effect
  useEffect(() => {
    let isMounted = true;
    return () => {
      isMounted = false;
    };
  }, [selectedDepotId, selectedDepot, selectedChargingStationId, depots, chargingStations, circuitData, loading]);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");

    const fetchRoutes = async () => {
      if (routes && Object.keys(routes).length > 0) {
        const fetchedRoutes = [];
        // Use Object.values to convert the routes object to an array
        for (const route of Object.values(routes)) {
          try {
            const response = await axios.get(API_ENDPOINTS.getRawRoute, {
              params: { route_id: route.route_id },
              headers: {
                Authorization: `Token ${authToken}`,
              },
            });

            if (response.data && response.data.links && Array.isArray(response.data.links)) {
              // Process all links in the route, not just the first one
              const allPositions = [];
              
              // Iterate through all links in the route
              response.data.links.forEach(link => {
                if (link && link.coordinates) {
                  // Convert coordinates from [lon, lat] to [lat, lng] for Google Maps
                  const positions = link.coordinates.map(coord => ({
                    lat: coord[1], 
                    lng: coord[0]
                  }));
                  allPositions.push(positions);
                }
              });
              
              fetchedRoutes.push({
                id: route.route_id,
                positions: allPositions, // Store all positions from all links
              });
            }
          } catch (error) {
            console.error(`Error fetching raw route for route ${route.route_id}:`, error);
          }
        }
        setAnalyticsRoutes(fetchedRoutes);
      } else {
        setAnalyticsRoutes([]);
      }
    };

    fetchRoutes();
  }, [routes]);

  // Determine the color for a route based on its electric status
  const getColorForRoute = (routeId) => {
    if (!routeColorCache.current[routeId]) {
      routeColorCache.current[routeId] = CIRCUIT_COLORS[Object.keys(routeColorCache.current).length % CIRCUIT_COLORS.length];
    }
    return routeColorCache.current[routeId];
  };

  // Helper function to validate coordinates
  const isValidCoordinate = (lat, lng) => {
    return typeof lat === 'number' && 
           typeof lng === 'number' && 
           !isNaN(lat) && 
           !isNaN(lng) &&
           lat >= -90 && 
           lat <= 90 && 
           lng >= -180 && 
           lng <= 180;
  };

  // Helper function to handle charging station click
  const handleChargingStationClick = (stationId) => {
    if (onChargingStationClick) {
      // Check if the station is already selected, if so, pass null to deselect it
      const shouldDeselect = stationId === selectedChargingStationId;
      onChargingStationClick(shouldDeselect ? null : stationId, true); // true indicates it's from the map
    }
  };

  // If a selectedRouteId is provided, filter analyticsRoutes to display only that route
  const routesToDisplay = selectedRouteId
    ? analyticsRoutes.filter(route => route.id === selectedRouteId)
    : [];

  // Handle map load
  const onLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMapRef(null);
  }, []);

  // Render circuit lines
  const renderCircuitLines = () => {
    if (!selectedDepotId || !circuitData[selectedDepotId]) {
      console.log('No circuit data available for rendering');
      return [];
    }

    const lines = [];
    
    circuitData[selectedDepotId].forEach((circuit, circuitIndex) => {
      if (circuit?.geometry?.coordinates) {
        const circuitColor = CIRCUIT_COLORS[circuitIndex % CIRCUIT_COLORS.length];
        circuit.geometry.coordinates.forEach((lineString, lineIndex) => {
          if (Array.isArray(lineString) && lineString.length >= 2) {
            const positions = lineString.map(coord => ({
              lat: coord[1], 
              lng: coord[0]
            }));
            
            lines.push(
              <Polyline
                key={`circuit-${selectedDepotId}-${circuitIndex}-${lineIndex}`}
                path={positions}
                options={{
                  strokeColor: circuitColor,
                  strokeWeight: 3,
                  strokeOpacity: 0.8,
                  clickable: true
                }}
                onClick={(e) => {
                  setActiveInfoWindow({
                    position: {
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng()
                    },
                    content: {
                      title: `Circuit: ${circuit.circuit_name || 'Unnamed Circuit'}`,
                      details: [
                        { label: 'Capacity', value: `${circuit.capacity || 'N/A'} kW` },
                        { label: 'Voltage', value: `${circuit.circuit_voltage || 'N/A'} kV` },
                        { label: 'Substation', value: circuit.substation_name ? circuit.substation_name.replace(/Kv/g, 'kV') : 'N/A' }
                      ]
                    }
                  });
                }}
              />
            );
          }
        });
      }
    });
    
    return lines;
  };

  // If the map is not loaded yet, show a loading message
  if (loadError) return <div className="p-4 text-red-500">Error loading maps. Please check your API key and network connection.</div>;
  if (!isLoaded) return <div className="p-4 text-gray-500">Loading Map...</div>;

  return (
    <div className="w-full h-full rounded-lg overflow-hidden shadow-lg">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: true,
          zoomControl: true
        }}
      >
        {loading && (
          <div className="map-loading-overlay">
            Loading circuit data...
          </div>
        )}
        
        {/* Only render circuit lines if we have a selectedDepotId */}
        {selectedDepotId && renderCircuitLines()}
        
        {/* Render depots */}
        {selectedDepotId && depots.map((depot) => {
          if (depot.depot_id !== selectedDepotId) {
            return null;
          }
          // Convert coordinates to numbers and validate them
          const lat = parseFloat(depot.lat);
          const lng = parseFloat(depot.lon);

          if (!isValidCoordinate(lat, lng)) {
            console.warn(`Invalid coordinates for depot ${depot.depot_id}:`, {
              latitude: depot.lat,
              longitude: depot.lon
            });
            return null;  // Skip this marker
          }

          return (
            <Marker
              key={`depot-${depot.depot_id}`}
              position={{ lat, lng }}
              icon={depot.depot_id === selectedDepotId ? icons?.depot?.selected : icons?.depot?.default}
              zIndex={depot.depot_id === selectedDepotId ? 200 : 100}
              onClick={() => {
                setActiveInfoWindow({
                  position: { lat, lng },
                  content: {
                    title: `Depot: ${depot.name || depot.depot_id}`,
                    details: [
                      { label: 'ID', value: depot.depot_id },
                      { label: 'Address', value: depot.address || 'N/A' },
                      { label: 'Coordinates', value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }
                    ]
                  }
                });
              }}
            />
          );
        })}
        
        {/* Render charging stations if showChargingStations is true */}
        {showChargingStations && selectedChargingStationId && chargingStations.map((station) => {
          if (station.cs_id !== selectedChargingStationId) {
            return null;
          }
          // Convert coordinates to numbers and validate them
          const lat = parseFloat(station.lat);
          const lng = parseFloat(station.lon);
          
          // Skip stations with missing IDs to prevent duplicate keys
          if (!station.cs_id) {
            return null;
          }

          if (!isValidCoordinate(lat, lng)) {
            console.warn(`Invalid coordinates for charging station ${station.cs_id}:`, {
              latitude: station.lat,
              longitude: station.lon
            });
            return null;  // Skip this marker
          }

          return (
            <Marker
              key={`charging-station-${station.cs_id}`}
              position={{ lat, lng }}
              icon={station.cs_id === selectedChargingStationId ? icons?.charger?.selected : icons?.charger?.default}
              zIndex={station.cs_id === selectedChargingStationId ? 150 : 50}
              onClick={() => {
                handleChargingStationClick(station.cs_id);
                setActiveInfoWindow({
                  position: { lat, lng },
                  content: {
                    title: `Charging Station: ${station.name || station.cs_id}`,
                    details: [
                      { label: 'ID', value: station.cs_id },
                      { label: 'Type', value: station.charger_type || 'Standard' },
                      { label: 'Power', value: `${station.power_capacity || 'N/A'} kW` },
                      { label: 'Coordinates', value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }
                    ]
                  }
                });
              }}
            />
          );
        })}
        
        {/* Only display routes if we have a selected route */}
        {routesToDisplay.map((route) => (
          // If positions is an array of arrays (multiple links)
          route.positions.map((positions, linkIndex) => (
            <Polyline
              key={`analytics-route-${route.id}-link-${linkIndex}`}
              path={positions}
              options={{
                strokeColor: getColorForRoute(route.id),
                strokeWeight: 4,
                strokeOpacity: 0.8,
                clickable: true
              }}
              onClick={(e) => {
                setActiveInfoWindow({
                  position: {
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng()
                  },
                  content: {
                    title: `Route: ${route.id}`,
                    details: [
                      { label: 'Link', value: linkIndex + 1 },
                      { label: 'Status', value: routes[route.id]?.is_electric === true ? 'Electric' : 'Non-Electric' }
                    ]
                  }
                });
              }}
            />
          ))
        ))}

        {/* Info Window for displaying details */}
        {activeInfoWindow && (
          <InfoWindow
            position={activeInfoWindow.position}
            onCloseClick={() => setActiveInfoWindow(null)}
          >
            <div className="p-2 font-sans max-w-xs">
              <h5>{activeInfoWindow.content.title}</h5>
              <table>
                <tbody>
                  {activeInfoWindow.content.details.map((detail, index) => (
                    <tr key={index}>
                      <td><strong>{detail.label}:</strong></td>
                      <td>{detail.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};